import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Check, X } from "lucide-react";

/**
 * QuickTagPicker — a keyboard-friendly, searchable picker rendered in a
 * shadcn Dialog. Used by Triage to attach a Client / Close / Task to an
 * email row in one stroke (from the right-click context menu or from the
 * c / p / t keyboard shortcuts on the active message).
 *
 * Props
 *   open          — boolean
 *   onOpenChange  — (next: boolean) => void
 *   title         — dialog heading (e.g. "Tag client")
 *   items         — [{ value, label, sub? }]
 *   currentId     — string | null — pre-selects this row
 *   onSelect      — (value: string) => void
 *   onClear       — () => void  (called when user picks "Unlink")
 *   emptyHint     — string shown when items is empty
 *   testId        — base data-testid (default "quick-tag")
 */
export default function QuickTagPicker({
  open,
  onOpenChange,
  title,
  items,
  currentId,
  onSelect,
  onClear,
  emptyHint = "Nothing to pick.",
  testId = "quick-tag",
}) {
  const [q, setQ] = useState("");
  useEffect(() => { if (open) setQ(""); }, [open]);
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return items;
    return (items || []).filter((it) => (it.label || "").toLowerCase().includes(needle));
  }, [items, q]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="p-0 max-w-md gap-0" data-testid={`${testId}-dialog`}>
        <DialogHeader className="px-4 pt-4 pb-2 border-b border-slate-100">
          <DialogTitle className="text-sm font-semibold tracking-tight">{title}</DialogTitle>
          <DialogDescription className="sr-only">
            Type to filter the list and press Enter to select. Press Escape to close.
          </DialogDescription>
        </DialogHeader>
        <Command shouldFilter={false} className="rounded-none">
          <CommandInput
            placeholder="Type to search…"
            value={q}
            onValueChange={setQ}
            data-testid={`${testId}-search`}
            autoFocus
          />
          <CommandList className="max-h-72">
            <CommandEmpty>{(items || []).length === 0 ? emptyHint : "No matches."}</CommandEmpty>
            <CommandGroup>
              {currentId && onClear && (
                <CommandItem
                  value="__clear__"
                  onSelect={() => { onClear(); onOpenChange(false); }}
                  data-testid={`${testId}-clear`}
                >
                  <X className="mr-2 h-3.5 w-3.5 text-rose-500"/>
                  <span className="text-rose-600">Clear current selection</span>
                </CommandItem>
              )}
              {filtered.map((it) => (
                <CommandItem
                  key={it.value}
                  value={`${it.label} ${it.value}`}
                  onSelect={() => { onSelect(it.value); onOpenChange(false); }}
                  data-testid={`${testId}-option-${it.value}`}
                >
                  <Check className={`mr-2 h-3.5 w-3.5 ${currentId === it.value ? "opacity-100" : "opacity-0"}`}/>
                  <div className="flex flex-col min-w-0">
                    <span className="truncate">{it.label}</span>
                    {it.sub && <span className="text-[11px] text-slate-500 truncate">{it.sub}</span>}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
